import { Button, Flex, Text, Textarea } from '@chakra-ui/react'
import { useCallback, useState } from 'react'
import { useForm, useFormState } from 'react-final-form'

interface LaborAmendmentFooterProps {
  onCancel: () => void
  /** Ref that onSubmit reads to get the reason. Set here before form.submit() fires. */
  amendmentReasonRef: React.RefObject<string | undefined>
}

// Renders below the entry form when editing an existing labor entry.
// Provider must enter an amendment reason before the Save button is enabled.
// This mirrors the antepartum AmendModal pattern but is inline rather than a pre-open modal,
// since labor entries are edited frequently and a blocking modal would slow bedside charting.
export const LaborAmendmentFooter = ({
  onCancel,
  amendmentReasonRef,
}: LaborAmendmentFooterProps) => {
  const form = useForm()
  const { submitting } = useFormState({ subscription: { submitting: true } })
  const [reason, setReason] = useState('')

  const canSave = reason.trim().length > 0

  const handleSave = useCallback(async () => {
    if (!canSave) return
    // Set the reason on the ref before form.submit() so onSubmit can read it.
    amendmentReasonRef.current = reason.trim()
    await form.submit()
  }, [canSave, reason, amendmentReasonRef, form])

  return (
    <Flex
      direction="column"
      gap={2}
      borderTop="1px solid"
      borderColor="gray.200"
      bg="gray.100"
      _dark={{ borderColor: 'gray.600', bg: 'gray.800' }}
      w="100%"
      px={3}
      py={2}>
      <Flex direction="column" gap={1}>
        <Text
          fontFamily="Hero-New"
          fontWeight={600}
          fontSize="xs"
          color="gray.600"
          _dark={{ color: 'gray.400' }}>
          Amendment reason{' '}
          <Text as="span" color="red.400">
            *
          </Text>
        </Text>
        <Textarea
          placeholder="Briefly describe what was changed and why…"
          value={reason}
          onChange={e => setReason(e.target.value)}
          size="sm"
          fontFamily="Hero-New"
          rows={2}
          resize="none"
          isDisabled={submitting}
        />
      </Flex>
      <Flex gap={2} justify="flex-end">
        <Button
          size="sm"
          variant="link"
          fontFamily="Hero-New"
          color="gray.500"
          onClick={onCancel}
          isDisabled={submitting}>
          CANCEL
        </Button>
        <Button
          size="sm"
          colorScheme="pink"
          fontFamily="Hero-New"
          isLoading={submitting}
          isDisabled={!canSave}
          onClick={handleSave}>
          Save Amendment
        </Button>
      </Flex>
    </Flex>
  )
}
